<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/vue'

interface IconOption {
  id: number
  name: string
  image_url: string | null
}

interface SelectedIcon {
  id: number
  name: string
  image_url: string | null
  description: string
  sort_order: number
}

const props = defineProps<{
  allIcons: IconOption[]
  selectedIcons?: SelectedIcon[]
  name?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: SelectedIcon[]]
}>()

const icons = ref<SelectedIcon[]>([])
const query = ref('')

onMounted(() => {
  console.log('All icons:', props.allIcons)
  if (props.selectedIcons && props.selectedIcons.length > 0) {
    icons.value = props.selectedIcons.map((icon, index) => ({
      ...icon,
      sort_order: icon.sort_order ?? index,
    }))
  }
})

watch(icons, (val) => {
  emit('update:modelValue', val)
}, { deep: true })

const filteredIcons = computed(() => {
  const q = query.value.toLowerCase()
  const selectedIds = new Set(icons.value.map(i => i.id))
  return props.allIcons.filter(icon =>
    !selectedIds.has(icon.id) &&
    (q === '' || icon.name.toLowerCase().includes(q))
  )
})

const selectedForCombobox = ref<IconOption | null>(null)

const addIcon = (icon: IconOption | null) => {
  if (!icon) return
  icons.value.push({
    id: icon.id,
    name: icon.name,
    image_url: icon.image_url,
    description: '',
    sort_order: icons.value.length,
  })
  selectedForCombobox.value = null
  query.value = ''
}

const removeIcon = (index: number) => {
  icons.value.splice(index, 1)
  recalcSortOrder()
}

const moveUp = (index: number) => {
  if (index === 0) return
  const tmp = icons.value[index]
  icons.value[index] = icons.value[index - 1]
  icons.value[index - 1] = tmp
  recalcSortOrder()
}

const moveDown = (index: number) => {
  if (index === icons.value.length - 1) return
  const tmp = icons.value[index]
  icons.value[index] = icons.value[index + 1]
  icons.value[index + 1] = tmp
  recalcSortOrder()
}

const recalcSortOrder = () => {
  icons.value.forEach((icon, i) => {
    icon.sort_order = i
  })
}

const jsonOutput = computed(() => JSON.stringify(icons.value.map(icon => ({
  id: icon.id,
  description: icon.description,
  sort_order: icon.sort_order,
}))))
</script>

<template>
  <div>
    <!-- Hidden input for legacy form submission (if name prop provided) -->
    <input v-if="name" type="hidden" :name="name" :value="jsonOutput">

    <!-- Icon Combobox for adding -->
    <div class="mb-3">
      <Combobox v-model="selectedForCombobox" @update:model-value="addIcon" nullable>
        <div class="relative">
          <div class="relative w-full">
            <ComboboxInput
              class="input input-bordered input-sm w-full"
              placeholder="Icon hinzufügen..."
              @change="query = $event.target.value"
              :display-value="() => query"
            />
            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center pr-2">
              <svg class="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z" clip-rule="evenodd" />
              </svg>
            </ComboboxButton>
          </div>
          <ComboboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-base-100 py-1 shadow-lg ring-1 ring-black ring-opacity-5">
            <div v-if="filteredIcons.length === 0" class="relative cursor-default select-none px-4 py-2 text-gray-500">
              Keine Icons gefunden.
            </div>
            <ComboboxOption
              v-for="icon in filteredIcons"
              :key="icon.id"
              :value="icon"
              v-slot="{ active }"
              as="template"
            >
              <li class="relative cursor-pointer select-none py-2 pl-3 pr-9 flex items-center gap-2" :class="{ 'bg-primary text-primary-content': active }">
                <img v-if="icon.image_url" :src="icon.image_url" class="w-6 h-6 object-contain" alt="" />
                <span class="w-6 h-6 bg-base-200 rounded" v-else></span>
                <span>{{ icon.name }}</span>
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>

    <!-- Selected icons list -->
    <div v-if="icons.length > 0" class="space-y-2">
      <div
        v-for="(icon, index) in icons"
        :key="icon.id"
        class="flex items-center gap-2 p-2 bg-base-200 rounded-lg"
      >
        <!-- Sort buttons -->
        <div class="flex flex-col gap-0.5">
          <button
            type="button"
            class="btn btn-ghost btn-xs px-1"
            :disabled="index === 0"
            @click="moveUp(index)"
            title="Nach oben"
          >
            <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clip-rule="evenodd" />
            </svg>
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-xs px-1"
            :disabled="index === icons.length - 1"
            @click="moveDown(index)"
            title="Nach unten"
          >
            <svg class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>

        <!-- Icon image -->
        <img v-if="icon.image_url" :src="icon.image_url" class="w-8 h-8 object-contain flex-shrink-0" alt="" />
        <span v-else class="w-8 h-8 bg-base-300 rounded flex-shrink-0"></span>

        <!-- Name & description -->
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium">{{ icon.name }}</div>
          <input
            type="text"
            v-model="icon.description"
            class="input input-bordered input-xs w-full mt-1"
            placeholder="Beschreibung (optional)"
            maxlength="255"
          />
        </div>

        <!-- Remove button -->
        <button
          type="button"
          class="btn btn-ghost btn-sm btn-square text-error flex-shrink-0"
          @click="removeIcon(index)"
          title="Entfernen"
        >
          <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
    <div v-else class="text-sm text-gray-500 italic">
      Keine Icons zugewiesen.
    </div>
  </div>
</template>
